import React, { useCallback } from 'react'
import _cx from 'classnames/bind'
import { Text, Modal, PopupTooltip, constants } from '@adc/ui-adc'
import { utils as bpUtils } from '@adc/blueprint'
import { match } from 'ts-pattern'
import type { Maybe, OnlineAuctionSegmentBid } from '@adc/sdk/types'
import ModalAddress from 'common/components/bidBanner/modalAddress'
import { NO_BIDDING_HISTORY } from 'common/statics'
import { CombinedListingType } from 'common/context/listing'
import cls from './styles.scss'

const { ColorNames } = constants

const cx = _cx.bind(cls)

const COUNTER_BID_TOOLTIP_LABEL = 'Seller Counterbid'
const COUNTER_BID_TOOLTIP_CONTENT_1 =
  'Counterbids are common at online and in-person auctions.'
const COUNTER_BID_TOOLTIP_CONTENT_2 =
  'Auction.com may bid on behalf of the Seller to help move the price closer to, but not higher than, Reserve.'
const CounterBidTooltip = () => {
  return (
    <PopupTooltip
      position='top center'
      wide='very'
      trigger={
        <Text
          as='span'
          className={cx('tooltip-label')}
          color='info'
          data-elm-id='counter_bid_tooltip_label'
          size='small'
        >
          <span className='u-mr-1'>{COUNTER_BID_TOOLTIP_LABEL}</span>
        </Text>
      }
      content={
        /* When changing the content, make sure to adjust the modal min-height. */
        <Text
          size='tiny'
          color='white'
          data-elm-id='counter_bid_tooltip_content'
        >
          <p>{COUNTER_BID_TOOLTIP_CONTENT_1}</p>
          <p>{COUNTER_BID_TOOLTIP_CONTENT_2}</p>
        </Text>
      }
      horizontalOffset={0}
      verticalOffset={0}
      offset={0}
    />
  )
}

interface BiddingHistoryModalProps {
  biddingHistory?: {
    bid: Maybe<OnlineAuctionSegmentBid>
    bidHistoryUnresolved: string
    number: number
  }[]
  listing: CombinedListingType
  onClose?: () => void
  open?: boolean
}

export const BiddingHistoryModal = ({
  biddingHistory = [],
  listing,
  onClose,
  open,
}: BiddingHistoryModalProps) => {
  const renderBidHistoryDescription = useCallback(
    (bidHistoryDescription: string) =>
      bpUtils.interpolate(bidHistoryDescription, {}, key =>
        match(key)
          .with('counteroffer', () => <CounterBidTooltip />)
          .with('your_bid', () => (
            <Text
              as='span'
              className={cx('current-user-bid')}
              color='gray'
              data-elm-id='user_bid'
              weight='heavy'
            >
              (you)
            </Text>
          ))
          .otherwise(() => '')
      ),
    []
  )

  return (
    <Modal
      elmId='bidding_history_modal'
      mobileTitleSkin='light'
      size='tiny'
      title='Bid History'
      style={{ minHeight: 350 }}
      open={open}
      onClose={onClose}
    >
      <Modal.Content>
        <ModalAddress addressTuple={listing.formatted_address} />

        {!biddingHistory.length && (
          <Text color='gray'>{NO_BIDDING_HISTORY}</Text>
        )}

        <div className={cx('body-1')}>
          {biddingHistory.map((bid, i) => {
            const { number, bidHistoryUnresolved } = bid
            return (
              <div
                key={i}
                className={cx('u-ml-2', 'u-pb-1', 'u-pt-1')}
                data-elm-id={`bid_history_bid_${number}`}
              >
                <Text
                  as='span'
                  color={ColorNames.Gray}
                  data-elm-id='bid_amount'
                >
                  {renderBidHistoryDescription(bidHistoryUnresolved)}
                </Text>
              </div>
            )
          })}
        </div>
      </Modal.Content>
    </Modal>
  )
}
